import { useTranslation } from "react-i18next";

import { safeLocale } from "@/utilities/i18n/safeLocale";

const DATE_TIME_OPTIONS: Intl.DateTimeFormatOptions = {
  year: "numeric",
  month: "numeric",
  day: "numeric",
  hour: "numeric",
  minute: "numeric",
};

const DATE_TIME_SECONDS_OPTIONS: Intl.DateTimeFormatOptions = { ...DATE_TIME_OPTIONS, second: "numeric" };

export interface FormatDateOptions {
  /** Also show the seconds, e.g. for single GPS points on a route. */
  seconds?: boolean;
}

/**
 * Formats a date the same way everywhere in the app.
 *
 * `language` comes from i18next, which can end up holding a stale or hand-edited value in
 * localStorage that is not a valid locale tag — `toLocaleString` throws a `RangeError` for that
 * rather than falling back on its own, so it is run through `safeLocale` (falls back to `de-DE`).
 */
export function formatDate(
  date: Date | string | undefined,
  language: string,
  { seconds = false }: FormatDateOptions = {},
): string {
  if (!date || date === "---") {
    return "---";
  }

  const parsed = typeof date === "string" ? new Date(date) : date;
  if (Number.isNaN(parsed.getTime())) {
    return "---";
  }

  return parsed.toLocaleString(safeLocale(language), seconds ? DATE_TIME_SECONDS_OPTIONS : DATE_TIME_OPTIONS);
}

/**
 * Returns the given date in a formatted string.
 * @param params
 * @returns
 */
export function useFormattedDate(date?: Date | string) {
  const { i18n } = useTranslation();
  return formatDate(date, i18n.language);
}
